import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Sparkles, Brain } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BehavioralFormPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/behavioral/${token}`)
      .then(r => { setData(r.data); if (r.data.status === "SUBMITTED") setResult({ done: true }); })
      .catch(e => setError(e.response?.data?.detail || "Link is invalid or expired."));
  }, [token]);

  const submit = async () => {
    if (!data) return;
    const missing = data.questions.filter(q => !answers[q.id]);
    if (missing.length > 0) { toast.error(`Please answer all ${data.questions.length} questions`); return; }
    setSubmitting(true);
    try {
      const { data: r } = await axios.post(`${API}/behavioral/${token}`, { answers });
      setResult(r);
      toast.success("Submitted — thank you!");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-neutral-50 grid place-items-center p-6">
      <Card className="max-w-md w-full border-0 shadow-md">
        <CardContent className="p-8 text-center">
          <div className="text-red-500 font-semibold mb-2">Unavailable</div>
          <p className="text-neutral-600 text-sm">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (!data) return <div className="min-h-screen grid place-items-center text-neutral-500">Loading…</div>;

  if (result) return (
    <div className="min-h-screen bg-neutral-50 grid place-items-center p-6">
      <Card className="max-w-md w-full border-0 shadow-md" data-testid="beh-thank-you">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-teal/10 text-teal grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-bold font-display">Thank you</h1>
          <p className="text-neutral-600 text-sm mt-2">
            Your responses have been submitted. Alfred is preparing a short brief for the hiring team.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4" data-testid="behavioral-form-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border text-xs uppercase tracking-widest text-neutral-500">
            <Brain className="w-3.5 h-3.5 text-pink" /> Behavioural assessment
          </div>
          <h1 className="text-3xl font-bold font-display mt-4">Hi {(data.candidate_name || "").split(" ")[0]} — a quick check-in</h1>
          <p className="text-neutral-600 text-sm mt-2">
            10 short statements. There are no right or wrong answers — pick what feels most true. Takes 3 minutes.
          </p>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 space-y-5">
            {data.questions.map((q, i) => (
              <div key={q.id} data-testid={`beh-q-${q.id}`} className="border-b last:border-b-0 pb-4 last:pb-0">
                <div className="text-sm mb-3">
                  <span className="text-pink font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {q.label}
                </div>
                <div className="flex gap-1.5 flex-wrap" role="radiogroup" data-testid={`beh-scale-${q.id}`}>
                  {data.likert.map(opt => {
                    const selected = answers[q.id] === opt.value;
                    return (
                      <button key={opt.value} onClick={() => setAnswers({ ...answers, [q.id]: opt.value })}
                        data-testid={`beh-${q.id}-${opt.value}`}
                        className={`flex-1 min-w-[100px] px-3 py-2 rounded-lg border text-xs transition-all ${selected ? "bg-pink text-white border-pink font-semibold" : "hover:bg-neutral-50"}`}>
                        <div className="font-mono text-[10px] opacity-70">{opt.value}</div>
                        <div className="mt-0.5">{opt.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button onClick={submit} disabled={submitting} className="btn-primary rounded-full px-8" data-testid="beh-submit-btn">
            <Sparkles className="w-4 h-4 mr-2" /> {submitting ? "Submitting…" : "Submit assessment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
